/**
 * UpgradeSheet — Free-tier upgrade bottom sheet
 *
 * Wraps the existing BottomSheet component. Triggered when free users hit
 * the Price Check limit, tap a premium-locked feature, or tap the
 * forensic roast unlock link.
 */

import { useEffect } from 'react';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { useSubscription } from '@/hooks/useSubscription';
import { T } from '@/styles/tokens';
import { trackEvent } from '@/services/telemetry/telemetry';

export type UpgradeTrigger = 'limit' | 'insights' | 'roast_link' | 'export' | 'bank_sync';

interface UpgradeSheetProps {
  isOpen: boolean;
  onClose: () => void;
  trigger: UpgradeTrigger;
}

const HEADLINES: Record<UpgradeTrigger, string> = {
  limit: 'Unlock Unlimited Price Checks',
  insights: 'Unlock Insights & Trends',
  roast_link: 'Unlock Forensic Analysis',
  export: 'Export Professional Quotes',
  bank_sync: 'Connect Your Bank',
};

const FEATURES: { label: string }[] = [
  { label: 'Unlimited Price Checks' },
  { label: '✦ Forensic Roast' },
  { label: 'Bank sync + burn tracking' },
  { label: 'Insights over time' },
  { label: 'Exportable quotes' },
];

export function UpgradeSheet({ isOpen, onClose, trigger }: UpgradeSheetProps) {
  const { subscribe, hasUsedTrial } = useSubscription();

  useEffect(() => {
    if (isOpen) {
      trackEvent({ eventType: 'upgrade_sheet_opened', screen: 'upgrade_sheet', meta: { trigger } });
    }
  }, [isOpen, trigger]);

  const handleClose = () => {
    trackEvent({ eventType: 'upgrade_sheet_dismissed', screen: 'upgrade_sheet', meta: { trigger } });
    onClose();
  };

  const handleCta = async () => {
    trackEvent({ eventType: 'upgrade_sheet_cta_tapped', screen: 'upgrade_sheet', meta: { trigger } });
    try {
      // Trial is attached to the Checkout Session server-side for first-time users.
      // Backing out of Checkout writes nothing, so there's no drift risk.
      await subscribe();
    } catch (err) {
      console.error('[UpgradeSheet] CTA failed:', err);
    }
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={handleClose}>
      <div style={{
        padding: '8px 24px 28px',
        fontFamily: T.fontFamily,
        textAlign: 'center',
      }}>
        {/* Sparkle icon (matches ForensicRoastTeaser pattern) */}
        <div style={{
          width: 48, height: 48, borderRadius: T.radiusMd,
          background: T.primarySubtle,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '8px auto 16px',
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
            stroke={T.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
          </svg>
        </div>

        {/* Headline */}
        <h2 style={{
          fontSize: 22, fontWeight: 700, color: T.text,
          margin: 0, letterSpacing: '-0.01em',
        }}>
          {HEADLINES[trigger]}
        </h2>

        {/* Subtitle */}
        <p style={{
          fontSize: 14, color: T.textSecondary,
          margin: '8px 0 24px', lineHeight: 1.5,
        }}>
          One corrected price pays for a year.
        </p>

        {/* Feature list */}
        <ul style={{
          listStyle: 'none', padding: 0, margin: '0 0 24px',
          textAlign: 'left',
        }}>
          {FEATURES.map((f, i) => (
            <li key={i} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 0',
              borderBottom: i < FEATURES.length - 1 ? `1px solid ${T.borderLight}` : 'none',
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke={T.primary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span style={{ fontSize: 14, fontWeight: 500, color: T.text }}>{f.label}</span>
            </li>
          ))}
        </ul>

        {/* Price block */}
        <div style={{ marginBottom: 20 }}>
          <div style={{
            fontSize: 28, fontWeight: 700, color: T.text,
            letterSpacing: '-0.02em',
          }}>
            $9.99<span style={{ fontSize: 16, color: T.textSecondary, fontWeight: 500 }}>/mo</span>
          </div>
          {!hasUsedTrial && (
            <div style={{ fontSize: 13, fontWeight: 600, color: T.primary, marginTop: 4 }}>
              7-day free trial
            </div>
          )}
        </div>

        {/* Primary CTA */}
        <button
          onClick={handleCta}
          style={{
            width: '100%',
            height: 52,
            borderRadius: T.radiusPill,
            border: 'none',
            background: T.primary,
            color: T.white,
            fontSize: 16,
            fontWeight: 600,
            letterSpacing: '-0.01em',
            cursor: 'pointer',
            fontFamily: T.fontFamily,
            boxShadow: `0 4px 16px ${T.primaryGlow}`,
          }}
        >
          {hasUsedTrial ? 'Subscribe' : 'Start Free Trial'}
        </button>

        {/* Dismiss link */}
        <button
          onClick={handleClose}
          style={{
            display: 'block',
            width: '100%',
            marginTop: 14,
            padding: 8,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 500,
            color: T.textSecondary,
            fontFamily: T.fontFamily,
          }}
        >
          Not now
        </button>
      </div>
    </BottomSheet>
  );
}

export default UpgradeSheet;
